import { ref, computed, readonly } from 'vue'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { realtimeClient } from '@/lib/realtimeservice'
import { useClientSettings } from '@/composables/useClientSettings'
import { filterVisibleChannels } from '@shared/channel-visibility'
import { getChannelServerGroup } from '@shared/channel-server-group'
import type { Database } from '@shared/db/public.types'

type ChannelRow = Database['public']['Tables']['channels']['Row']
type DevChannelRow = ChannelRow & {
  is_dev_channel: true
  dev_origin: string
}

type WorkspaceChannel = ChannelRow | DevChannelRow

export interface IframeBuffer {
  id: number
  url: string
  channelId: string
  lastAccessed: number
}

export class WorkspaceManager {
  private static instance: WorkspaceManager | null = null

  // State
  private _channels = ref<WorkspaceChannel[]>([])
  private _projects = ref<any[]>([])
  private _selectedChannelId = ref('')
  private _channelProcesses = ref<Map<string, any[]>>(new Map())
  private _processesLastUpdated = ref(Date.now())
  private _buffers = ref<IframeBuffer[]>([])
  private _activeBufferId = ref<number>(-1)
  private _isLoading = ref(false)
  private _loadingTimeout: any = null

  private isDev = import.meta.env.DEV
  private LAST_CHANNEL_KEY = 'radsuite_last_channel'

  // Getters
  public get channels() { return readonly(this._channels) }
  public get projects() { return readonly(this._projects) }
  public get selectedChannelId() { return readonly(this._selectedChannelId) }
  public get channelProcesses() {
    this._processesLastUpdated.value
    return readonly(this._channelProcesses)
  }
  public get buffers() { return readonly(this._buffers) }
  public get activeBufferId() { return readonly(this._activeBufferId) }
  public get isLoading() { return readonly(this._isLoading) }

  private constructor() {
    this.initRealtimeListeners()
    this.initBuffers()
  }

  private getMaxChannels(): number {
    const { settings } = useClientSettings()
    return settings.value.maxLoadedChannels
  }

  private initBuffers() {
    const count = this.getMaxChannels()
    this.resizeBuffers(count)
  }

  public resizeBuffers(count: number) {
    const current = this._buffers.value
    if (current.length === count) return

    if (current.length < count) {
      // Add new buffers
      for (let i = current.length; i < count; i++) {
        current.push({ id: i, url: '', channelId: '', lastAccessed: 0 })
      }
    } else {
      // Remove excess buffers (keep most recently used)
      const sorted = [...current].sort((a, b) => b.lastAccessed - a.lastAccessed)
      const toKeep = sorted.slice(0, count)
      
      // If active buffer is being removed, switch to first kept buffer
      if (!toKeep.find(b => b.id === this._activeBufferId.value)) {
        const active = toKeep.find(b => b.channelId === this._selectedChannelId.value)
        this._activeBufferId.value = active ? active.id : (toKeep[0]?.id ?? -1)
      }
      
      this._buffers.value = toKeep.sort((a, b) => a.id - b.id)
    }
  }

  private initRealtimeListeners() {
    realtimeClient.on('invalidation', async (data: { table: string, accountId: string }) => {
      console.log('WorkspaceManager: Received invalidation:', data)
      if (data.table === 'channels') {
        await this.refreshChannels()
      }
    })
  }

  public static getInstance(): WorkspaceManager {
    if (!this.instance) {
      this.instance = new WorkspaceManager()
      this.instance.initMessageListener()
    }
    return this.instance
  }

  private initMessageListener() {
    if (typeof window === 'undefined') return

    window.addEventListener('message', (event) => {
      const data = event.data
      if (data && data.type === 'coo-processes-update') {
        const origin = event.origin
        console.log(`WorkspaceManager: Received coo-processes-update from origin: ${origin}`)

        const channel = this._channels.value.find(c => {
          const sanitizedId = this.sanitize(c.channel_id || c.id)
          const matches = origin.includes(sanitizedId)
          console.log(`WorkspaceManager: Checking channel ${c.id} (sanitized: ${sanitizedId}) against origin: ${matches ? 'MATCH' : 'NO MATCH'}`)
          return matches
        })

        if (channel) {
          const processes = data.payload?.processes || data.processes || []
          console.log(`WorkspaceManager: Received process update for channel ${channel.id} (${channel.channel_id}). Processes:`, processes)
          this._channelProcesses.value.set(channel.id, processes)
          this._processesLastUpdated.value = Date.now()
        } else {
          console.warn(`WorkspaceManager: Could not find matching channel for origin: ${origin}. Available channels:`, this._channels.value.map(c => ({ id: c.id, sanitized: this.sanitize(c.channel_id || c.id) })))
        }
      }
    })
  }

  private sanitize(str: string): string {
    if (!str) return ''
    return str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  }

  private getChannelSettings(channel: WorkspaceChannel): Record<string, unknown> {
    return channel.settings && typeof channel.settings === 'object' && !Array.isArray(channel.settings)
      ? channel.settings as Record<string, unknown>
      : {}
  }

  private getChannelByocSettings(channel: WorkspaceChannel): Record<string, unknown> {
    const settings = this.getChannelSettings(channel)
    return settings.byoc && typeof settings.byoc === 'object' && !Array.isArray(settings.byoc)
      ? settings.byoc as Record<string, unknown>
      : {}
  }

  private getChannelIframePort(channel: WorkspaceChannel): number {
    const byoc = this.getChannelByocSettings(channel)
    const routes = Array.isArray(byoc.routes) ? byoc.routes : []
    const route = routes.find((item): item is { port: number, kind?: string } => {
      return Boolean(
        item
        && typeof item === 'object'
        && 'port' in item
        && typeof item.port === 'number'
        && item.port >= 1
        && item.port <= 65535
        && item.kind === 'viz'
      )
    }) || routes.find((item): item is { port: number, kind?: string } => {
      return Boolean(
        item
        && typeof item === 'object'
        && 'port' in item
        && typeof item.port === 'number'
        && item.port >= 1
        && item.port <= 65535
      )
    })

    return route?.port || 6001
  }

  private getCurrentSubjectContext() {
    const authStore = useAuthStore()
    return {
      userId: authStore.user?.id,
      accountRole: authStore.accountRole
    }
  }

  private getVisibleChannels(channels: ChannelRow[]): ChannelRow[] {
    return filterVisibleChannels(channels, this.getCurrentSubjectContext())
  }

  private getDevChannels(): DevChannelRow[] {
    if (typeof window === 'undefined') return []

    const params = new URLSearchParams(window.location.search)
    const devChannel = [...params.entries()]
      .find(([key]) => key.toLowerCase() === 'devchannel')?.[1]

    if (!devChannel || !['1', '2', '3'].includes(devChannel)) {
      return []
    }

    const devChannelHost = window.location.hostname
    const devChannelConfigs: Array<{ suffix: string; origin: string }> = []

    if (devChannel === '1' || devChannel === '3') {
      devChannelConfigs.push({ suffix: '1', origin: `http://${devChannelHost}:6001` })
    }

    if (devChannel === '2' || devChannel === '3') {
      devChannelConfigs.push({ suffix: '2', origin: `http://${devChannelHost}:7001` })
    }

    return devChannelConfigs.map(({ suffix, origin }) => ({
      id: `devchannel-local-${suffix}`,
      account_id: '',
      channel_id: `Local Dev Channel ${suffix}`,
      compute_profile_id: null,
      container_image_id: null,
      created_at: new Date(0).toISOString(),
      created_by: null,
      environment_id: null,
      guild_id: null,
      hidden: false,
      is_active: true,
      is_private: false,
      name: `Local Dev Channel ${suffix}`,
      project_id: null,
      type: 'rad',
      settings: {},
      is_dev_channel: true,
      dev_origin: origin,
      release: 'dev'
    }))
  }

  public getUrlForChannel(channelId: string): string {
    const authStore = useAuthStore()
    if (!channelId) return ''

    const channel = this._channels.value.find(c => c.id === channelId)
    if (!channel) return ''

    if ('is_dev_channel' in channel && channel.is_dev_channel) {
      const token = authStore.session?.access_token
      if (token) {
        return `${channel.dev_origin}/?token=${encodeURIComponent(token)}`
      }
      return `${channel.dev_origin}/`
    }

    if (!authStore.account?.id) return ''

    const serverGroup = getChannelServerGroup(channel)
    const port = this.getChannelIframePort(channel)
    const channelIdNoDashes = this.sanitize(channel.id)
    const rawUrl = `https://c-${channelIdNoDashes}-${serverGroup}-${port}.devpreview.radsuite.com/`
    let url = `/channels/${encodeURIComponent(channel.id)}/open?url=${encodeURIComponent(rawUrl)}`

    const isLocalDev = window.location.hostname.includes('localhost') || window.location.hostname.includes('www-dev')
    if (this.isDev || isLocalDev) {
      const token = authStore.session?.access_token
      if (token) {
        const previewUrl = new URL(rawUrl)
        previewUrl.searchParams.set('token', token)
        url = previewUrl.toString()
      }
    }

    return url
  }

  public async selectChannel(channelId: string) {
    if (this._selectedChannelId.value === channelId) return

    // Ensure buffers are sized correctly (in case settings changed)
    this.resizeBuffers(this.getMaxChannels())

    const newUrl = this.getUrlForChannel(channelId)
    
    this._selectedChannelId.value = channelId

    // Check if already loaded in any buffer
    const existingBuffer = this._buffers.value.find(b => b.channelId === channelId && b.url === newUrl && b.url !== '')
    if (existingBuffer) {
      this._activeBufferId.value = existingBuffer.id
      existingBuffer.lastAccessed = Date.now()
      this._isLoading.value = false
      if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
      console.log(`WorkspaceManager: Channel already loaded in buffer ${existingBuffer.id}, activating.`)
      this.saveLastChannel(channelId)
      return
    }

    // Find the best buffer to use
    let targetBuffer = this._buffers.value.find(b => !b.channelId && !b.url)

    if (!targetBuffer) {
      // Evict the least recently used non-active buffer
      const inactiveBuffers = this._buffers.value.filter(b => b.id !== this._activeBufferId.value)
      if (inactiveBuffers.length > 0) {
        targetBuffer = inactiveBuffers.sort((a, b) => a.lastAccessed - b.lastAccessed)[0]
      } else {
        // All buffers active or only one buffer - reuse the active one
        targetBuffer = this._buffers.value[0]
      }
    }

    if (targetBuffer) {
      this._isLoading.value = true
      if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
      this._loadingTimeout = setTimeout(() => {
        if (this._isLoading.value) {
          console.warn('WorkspaceManager: Iframe load timed out, hiding spinner')
          this._isLoading.value = false
        }
      }, 10000)

      targetBuffer.url = newUrl
      targetBuffer.channelId = channelId
      targetBuffer.lastAccessed = Date.now()
      // Don't activate yet - wait for onIframeLoad
    }

    this.saveLastChannel(channelId)
  }

  private saveLastChannel(channelId: string) {
    const authStore = useAuthStore()
    if (!authStore.account?.id) return
    try {
      const existing = localStorage.getItem(this.LAST_CHANNEL_KEY)
      const data = existing ? JSON.parse(existing) : {}
      data[authStore.account.id] = channelId
      localStorage.setItem(this.LAST_CHANNEL_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Error saving last channel:', error)
    }
  }

  public async loadData() {
    const authStore = useAuthStore()
    const requestedAccountId = authStore.activeAccountId
    if (!requestedAccountId) return

    try {
      const [{ data: projectsData }, { data: channelsData }] = await Promise.all([
        supabase.from('projects').select('*').eq('account_id', requestedAccountId),
        supabase.from('channels').select('*').eq('account_id', requestedAccountId).eq('type', 'rad').eq('is_active', true)
      ])

      if (authStore.activeAccountId !== requestedAccountId) {
        return
      }

      this._projects.value = projectsData || []
      this._channels.value = [...this.getDevChannels(), ...this.getVisibleChannels(channelsData || [])]

      // Smart channel selection logic
      let targetChannel = ''
      try {
        const existing = localStorage.getItem(this.LAST_CHANNEL_KEY)
        if (existing) {
          const data = JSON.parse(existing)
          const lastChannel = data[requestedAccountId]
          if (lastChannel && this._channels.value.find(c => c.id === lastChannel)) {
            targetChannel = lastChannel
          }
        }
      } catch (error) {}
      
      const devChannel = this._channels.value.find((channel) => 'is_dev_channel' in channel && channel.is_dev_channel)
      if (devChannel) {
        targetChannel = devChannel.id
      }

      if (!targetChannel && this._channels.value.length > 0) {
        const firstPublicChannel = this._channels.value.find(c => !c.is_private) || this._channels.value[0]
        targetChannel = firstPublicChannel.id
      }
      
      if (targetChannel) {
        const initialUrl = this.getUrlForChannel(targetChannel)
        this._selectedChannelId.value = targetChannel
        
        // Ensure we have buffers
        this.resizeBuffers(this.getMaxChannels())
        
        if (this._buffers.value.length > 0) {
          this._buffers.value[0].url = initialUrl
          this._buffers.value[0].channelId = targetChannel
          this._buffers.value[0].lastAccessed = Date.now()
          this._activeBufferId.value = this._buffers.value[0].id
        }
        
        this._isLoading.value = true

        if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
        this._loadingTimeout = setTimeout(() => {
          if (this._isLoading.value) this._isLoading.value = false
        }, 10000)
      }

      realtimeClient.connect()

    } catch (error) {
      console.error('Error loading workspace data:', error)
    }
  }

  private async refreshChannels() {
    const authStore = useAuthStore()
    if (!authStore.account) return

    const { data: channelsData } = await supabase
      .from('channels')
      .select('*')
      .eq('account_id', authStore.account.id)
      .eq('type', 'rad')
      .eq('is_active', true)

    if (channelsData) {
      this._channels.value = [...this.getDevChannels(), ...this.getVisibleChannels(channelsData)]

      if (this._selectedChannelId.value && !this._channels.value.find(c => c.id === this._selectedChannelId.value)) {
        if (this._channels.value.length > 0) {
          const firstPublicChannel = this._channels.value.find(c => !c.is_private) || this._channels.value[0]
          this.selectChannel(firstPublicChannel.id)
        } else {
          this._selectedChannelId.value = ''
          this._buffers.value.forEach(b => { b.url = ''; b.channelId = '' })
          this._activeBufferId.value = -1
        }
      }
    }
  }

  public onIframeLoad(bufferId: number) {
    const buffer = this._buffers.value.find(b => b.id === bufferId)
    if (!buffer) return

    if (buffer.channelId && buffer.channelId === this._selectedChannelId.value) {
      // This buffer loaded the currently selected channel — make it active
      this._activeBufferId.value = bufferId
      this._isLoading.value = false
      buffer.lastAccessed = Date.now()
      if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
      console.log(`WorkspaceManager: Switched to buffer ${bufferId} (channel: ${buffer.channelId})`)
    } else if (this._isLoading.value && this._activeBufferId.value === bufferId) {
      // Currently active buffer finished loading (e.g., reload) — just clear spinner
      this._isLoading.value = false
      if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
    }
  }

  public async reloadChannel(channelId: string, delayMs: number = 5000) {
    this._isLoading.value = true

    if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
    this._loadingTimeout = setTimeout(() => {
      if (this._isLoading.value) {
        console.warn('WorkspaceManager: Reload timed out')
        this._isLoading.value = false
      }
    }, delayMs + 15000)

    setTimeout(() => {
      const url = this.getUrlForChannel(channelId)
      if (!url) {
        this._isLoading.value = false
        return
      }

      const busterUrl = url.includes('?') ? `${url}&rb=${Date.now()}` : `${url}?rb=${Date.now()}`

      const activeBuffer = this._buffers.value.find(b => b.id === this._activeBufferId.value)
      if (activeBuffer) {
        activeBuffer.url = busterUrl
      }
    }, delayMs)
  }
}

export function useWorkspaceManager() {
  return WorkspaceManager.getInstance()
}
